import { useCallback } from "react";
import { useSelector } from "react-redux";
import { resolveDealScopeItems } from "../catalog";

/**
 * A stable "no such map". 🔴 Not a `|| {}` inside the selector: a fresh object per call makes
 * `useSelector` report a change on every store action. On web both maps exist, so that never
 * fired while the deals sheet was this hook's only caller — but mobile's items store has
 * neither, and the customer summary (both platforms) calls it since 2026-09-24.
 */
const NO_MAP: Record<string, any> = {};

/**
 * `(deal) => the menu items it may be redeemed against`, for the CURRENT channel.
 *
 * Returns a stable callback rather than a list because the caller has many deals and asks per
 * deal — recomputing the whole catalogue for each row is what this shape avoids.
 *
 * The rule itself is `resolveDealScopeItems`; this hook is only the store read, so the scope
 * logic stays testable without a store.
 *
 * ⚠️ On MOBILE it answers `[]` for every deal: `itemsStore` there has no `channelToItemMap` or
 * `updatedChannelItemsData`. A caller that renders on both platforms gets no item thumbnails and
 * no "Choose 1 of N" on mobile, rather than the wrong ones.
 */
export function useDealEligibleItems(): (deal: any) => any[] {
  const channelToItemMap = useSelector((state: any) => state.itemsStore?.channelToItemMap || NO_MAP);
  const itemData = useSelector((state: any) => state.itemsStore?.updatedChannelItemsData || NO_MAP);
  const channelId = useSelector((state: any) => state.saleDataStore?.selectedChannelData?.channelId);

  return useCallback(
    (deal: any = {}) =>
      resolveDealScopeItems(deal, { channelMap: channelToItemMap?.[channelId] || {}, itemData }),
    [channelToItemMap, itemData, channelId],
  );
}
